import type { ReactNode } from "react";
import { movementGlyphs } from "../lib/movement-paths.js";
import {
  BoxSelect,
  Copy,
  Crop,
  ExternalLink,
  History,
  Library,
  Lock,
  MousePointer2,
  MoveUpRight,
  Plus,
  Redo2,
  RotateCcw,
  Route,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  Type,
  Undo2,
  Upload,
} from "lucide-react";
import { UtilityIcon } from "../utility/UtilityIcon.js";
import {
  Chip,
  Select,
  Frames,
  Radar,
  Rail,
  Row,
  route,
  routePoints,
} from "./illustration-parts.js";
import type { IllustrationKey } from "./onboarding-steps.js";

/** Mini-Ansichten für den Taktik-Editor. */
export const editorScenes: Partial<Record<IllustrationKey, () => ReactNode>> = {
  readonly: () => (
    <div className="ob-stack">
      <Chip tone="warning" hl>
        <Lock /> Nur lesen
      </Chip>
      <span className="ob-input is-disabled">Taktikname</span>
      <span className="ob-input is-disabled">Aufgabe</span>
    </div>
  ),
  tactics: () => (
    <div className="ob-stack">
      <Row>
        <span className="ob-input">
          <Search /> Name, Kategorie oder Tag…
        </span>
        <Chip tone="primary" hl>
          <Plus /> NEUE TAKTIK
        </Chip>
      </Row>
      <span className="ob-panel">
        {["A-Execute", "B-Split"].map((name) => (
          <Row key={name}>
            {name}
            <span className="ob-grow" />
            <Copy />
            <Trash2 />
          </Row>
        ))}
      </span>
    </div>
  ),
  draw: () => (
    <Radar
      overlay={
        <>
          <Rail>
            <MousePointer2 />
            <Route className="ob-hl" />
            <MoveUpRight />
            <Type />
            <BoxSelect />
          </Rail>
          <span className="ob-corner is-top">
            <Undo2 />
            <Redo2 />
          </span>
        </>
      }
    >
      <rect x="182" y="18" width="76" height="28" className="ob-area" />
      <path d={route} className="ob-route" />
      {routePoints.map(([x, y]) => (
        <circle key={x} cx={x} cy={y} r="3.5" className="ob-node" />
      ))}
      <path d="M107 27 L145 43" className="ob-arrow-line" />
      <text x="66" y="24" className="ob-map-text">
        Warten
      </text>
    </Radar>
  ),
  "route-floors": () => (
    <Radar
      overlay={
        <>
          <span className="ob-corner is-top">
            <Chip tone="route" hl>
              EBENE 1/2
            </Chip>
          </span>
          <span className="ob-corner">
            <Chip tone="muted">BILD↑ · BILD↓</Chip>
          </span>
        </>
      }
    >
      <path d="M43 80 L102 61 L150 61" className="ob-route" />
      <path d="M150 61 L220 35" className="ob-route is-covered" />
      {[
        [43, 80],
        [220, 35],
      ].map(([x, y]) => (
        <circle key={x} cx={x} cy={y} r="3.5" className="ob-node" />
      ))}
      <g transform="translate(102 61)" className="ob-hl">
        <circle r="7" className="ob-node" />
        <path
          d={movementGlyphs.jump}
          transform="translate(-5.5 -5.5) scale(0.46)"
          className="ob-route ob-glyph"
        />
      </g>
    </Radar>
  ),
  "utility-place": () => (
    <Radar
      overlay={
        <Rail>
          <UtilityIcon type="SMOKE" />
          <UtilityIcon type="FLASH" />
          <UtilityIcon type="MOLLY" />
          <UtilityIcon type="HE" />
        </Rail>
      }
    >
      <path d="M102 73 Q161 5 225 47" className="ob-arc" />
      <circle cx="102" cy="73" r="5" className="ob-node" />
      <circle cx="225" cy="47" r="12" className="ob-smoke is-hl" />
    </Radar>
  ),
  library: () => (
    <Row>
      <Chip tone="route" hl>
        Schritt 2
      </Chip>
      <span className="ob-arrow">→</span>
      <span className="ob-panel">
        <small>
          <Library /> Gespeicherte Granaten
        </small>
        <Row>
          <UtilityIcon type="FLASH" /> Pop-Flash A
        </Row>
        <Chip tone="primary" hl>
          ZU SCHRITT HINZUFÜGEN
        </Chip>
      </span>
    </Row>
  ),
  inspector: () => (
    <div className="ob-stack">
      <span className="ob-segment">
        <span>TAKTIK</span>
        <span className="is-on">SPIELER</span>
      </span>
      <Row>
        <span className="ob-input">ROLLE · Entry</span>
        <span className="ob-input">AUFGABE</span>
      </Row>
      <Row>
        <Frames hl />
        <span className="ob-icon-btn">
          <Upload />
        </span>
        <span className="ob-icon-btn">
          <Crop />
        </span>
      </Row>
    </div>
  ),
  save: () => (
    <span className="ob-bar">
      <Select label="STATUS" value="Entwurf" />
      <span className="ob-grow" />
      <Chip>
        <ExternalLink /> Vorschau
      </Chip>
      <Chip tone="primary" hl>
        <Save /> Speichern
      </Chip>
    </span>
  ),
  snapshots: () => (
    <span className="ob-panel is-wide">
      <small>
        <History /> VERÖFFENTLICHUNGEN
      </small>
      <Row>
        REV 12{" "}
        <Chip tone="route" hl>
          · AKTIV
        </Chip>
      </Row>
      <Row>REV 11</Row>
      <Row>REV 10</Row>
    </span>
  ),
  publish: () => (
    <Row>
      <Select label="STATUS" value="Freigegeben" hl />
      <span className="ob-arrow">→</span>
      <Chip>
        <Save /> Speichern
      </Chip>
      <span className="ob-arrow">→</span>
      <Chip tone="primary" hl>
        Veröffentlichen · Rev 13
      </Chip>
    </Row>
  ),
  rollback: () => (
    <span className="ob-panel is-wide">
      <small>
        <History /> VERÖFFENTLICHUNGEN
      </small>
      <Row>
        REV 12 <Chip tone="route">· AKTIV</Chip>
      </Row>
      <Row>
        REV 11
        <span className="ob-grow" />
        <Chip tone="primary" hl>
          <RotateCcw /> AKTIVIEREN
        </Chip>
      </Row>
    </span>
  ),
  eligibility: () => (
    <span className="ob-panel is-wide">
      <small>SPIELBERECHTIGT · P3</small>
      <Row>
        <Chip tone="route" hl>
          <ShieldCheck /> anna
        </Chip>
        <Chip tone="route">
          <ShieldCheck /> ben
        </Chip>
        <Chip tone="muted">cem</Chip>
      </Row>
    </span>
  ),
  callouts: () => (
    <Radar
      overlay={
        <span className="ob-corner is-top">
          <span className="ob-segment is-small">
            <span>Taktik</span>
            <span className="is-on">Callouts</span>
          </span>
        </span>
      }
    >
      <text x="64" y="74" className="ob-callout">
        Jungle
      </text>
      <text x="150" y="40" className="ob-callout is-hl">
        Palace
      </text>
      <text x="214" y="82" className="ob-callout">
        A Site
      </text>
    </Radar>
  ),
};
